import { memo, useCallback, useState, Fragment, useMemo } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { assetsClipDownloadTranscriptRetrieve } from '@goldcast/api/content';
import IconButton from '@/components/atoms/Button/IconButton';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { downloadURI } from '@/libs/core';
import { useAppContext } from '@/context/AppContext/AppContext';
import Icon, { IconName } from '@/components/atoms/Icon';
import { getErrorDiagnostics } from '@/libs/errorDiagnostics';

const TranscriptDownloadButton = ({ clipId }: { clipId: string }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { logger } = useAppContext();

  const downloadTranscript = useCallback(
    (format = 'DOCX') => {
      if (!clipId) {
        showErrorToast('Clip ID not found');
        return;
      }

      setIsDownloading(true);

      assetsClipDownloadTranscriptRetrieve({
        id: clipId,
        queryParams: {
          doc_type: format
        }
      })
        .then(response => {
          if (!response.url) {
            throw new Error('Transcript download URL not found');
          }

          downloadURI(response.url);
          showSuccessToast('Transcript download started!');
        })
        .catch(error => {
          logger.error('[TranscriptDownloadButton] Failed to download transcript', {
            clipId,
            format,
            ...getErrorDiagnostics(error)
          });
          showErrorToast('Failed to download transcript');
        })
        .finally(() => {
          setIsDownloading(false);
        });
    },
    [clipId, logger]
  );

  const downloadVTT = useCallback(() => {
    downloadTranscript('VTT');
  }, [downloadTranscript]);

  const downloadDOCX = useCallback(() => {
    downloadTranscript('DOCX');
  }, [downloadTranscript]);

  const downloadOptions = useMemo(
    () => [
      {
        onClick: downloadVTT,
        iconName: 'IconBadgeCc' as IconName,
        label: 'WebVTT',
        extension: 'vtt',
        testId: 'transcript-download-vtt'
      },
      {
        onClick: downloadDOCX,
        iconName: 'IconFileText' as IconName,
        label: 'Word',
        extension: 'docx',
        testId: 'transcript-download-docx'
      }
    ],
    [downloadVTT, downloadDOCX]
  );

  return (
    <div className="relative">
      <Menu as="div" className="relative inline-block text-left">
        <div>
          <Menu.Button as="div" className="inline-flex">
            <IconButton
              icon={isDownloading ? 'IconLoader2' : 'IconDownload'}
              buttonClassName="h-8 rounded"
              iconClassName={isDownloading ? 'animate-spin' : ''}
              disabled={isDownloading}
              trackingId="transcript-download-button"
              title="Download Transcript (VTT/DOCX)"
              size="base"
            />
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-lg border bg-white p-1 shadow-lg focus:outline-none">
            <div className="space-y-0.5">
              {downloadOptions.map(({ onClick, iconName, label, extension, testId }) => (
                <Menu.Item key={testId}>
                  {({ active }) => (
                    <button
                      onClick={onClick}
                      className={`${
                        active ? 'bg-gray-50' : ''
                      } flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm`}
                      data-testid={testId}
                    >
                      <Icon name={iconName} size={16} />
                      {label}
                      <span className="ml-auto text-xs text-slate-400">{extension}</span>
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
};

export default memo(TranscriptDownloadButton);
